import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import AOS from "aos";
import "aos/dist/aos.css";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./styles/theme.jsx";

import Home from "./pages/Home";
import Header from "./components/layout/header/header";
import Footer from "./components/layout/footer/footer.jsx";
import ScrollToTop from "./components/other/ScrollToTop.jsx";
import ProtectedDashboardRoute from "./components/dashboard/ProtectedDashboardRoute.jsx";

// import { CartProvider } from "./context/CartContext.js";

import { NotFound, NotFoundDash } from "./pages/NotFound";
import ProductPage from "./components/search/SearchableProductPage";
import SearchLandingPage from "./pages/SearchLanding";
import SearchMetadata from "./components/search/SearchMetadata";
import { publicPages } from "./utils/searchMetadata";

const LoginPage = lazy(() => import("./pages/Auth/Login/index.jsx"));
const SignupPage = lazy(() => import("./pages/Auth/SignUp/index.jsx"));
const PDFViewPage = lazy(() => import("./components/OrderPDFTemplate.jsx"));
const Dashboard = lazy(() => import("./pages/Dashboard/"));
const Calculator = lazy(() => import("./pages/Dashboard/Calculator/index.jsx"));
const SettingsPage = lazy(() => import("./pages/Dashboard/Settings/index.jsx"));
const MyMotiv = lazy(() => import("./pages/Dashboard/Motiv/index.jsx"));
const CreateMotif = lazy(() => import("./pages/Dashboard/Motiv/createMotif.jsx"));
const MotifPage = lazy(() => import("./pages/Dashboard/Motiv/motifPage.jsx"));
const AdminMyMotif = lazy(() => import("./pages/Dashboard/Admin/Motiv/index.jsx"));
const AdminPage = lazy(() => import("./pages/Dashboard/Admin/index.jsx"));
const AdminCreateUser = lazy(() => import("./pages/Dashboard/Admin/CreateUser/index.jsx"));
const PriceChart = lazy(() => import("./pages/Dashboard/Admin/PriceChart/index.jsx"));
const EditMotif = lazy(() => import("./pages/Dashboard/Motiv/editMotif.jsx"));
const CartPage = lazy(() => import("./pages/Checkout/index.jsx"));
const OrderHistory = lazy(() => import("./pages/Dashboard/OrderHistory/index.jsx"));
const ProductsPage = lazy(() => import("./pages/Dashboard/Products/index.jsx"));
const OrderDetails = lazy(() => import("./pages/Dashboard/OrderHistory/orderDetails.jsx"));
const AdminOrderHistory = lazy(() => import("./pages/Dashboard/OrderHistory/adminIndex.jsx"));
const UsersPage = lazy(() => import("./pages/Dashboard/Admin/Users/index.jsx"));
const UserRecovery = lazy(() => import("./pages/Auth/UserRecovery/index.jsx"));
const PasswordResetPage = lazy(() => import("./pages/Auth/UserRecovery/PassReset.jsx"));
const TermsPage = lazy(() => import("./pages/Legal/terms.jsx"));
const OrderWizardPage = lazy(() => import("./pages/Order/index.jsx"));
const OrderConfirmationPage = lazy(() => import("./pages/OrderConfirmation/index.jsx"));
const CompaniesPage = lazy(() => import("./pages/Dashboard/Admin/Companies/index.jsx"));
const CompanyDetailsPage = lazy(() => import("./pages/Dashboard/Admin/Companies/CompanyDetailsPage.jsx"));
const ProductDesignPage = lazy(() => import("./pages/Dashboard/Products/ProductPage.jsx"));
const AdminAllProducts = lazy(() => import("./pages/Dashboard/Admin/Products/index.jsx"));
const MagicLoginPage = lazy(() => import("./pages/Auth/MagicLogin/index.jsx"));

function AOSRouteRefresh() {
  const location = useLocation();

  useEffect(() => {
    AOS.refreshHard();
  }, [location.pathname, location.hash]);

  return null;
}

function App() {
  useEffect(() => {
    AOS.init({
      // Global settings:
      disable: false,
      startEvent: "DOMContentLoaded",
      initClassName: "aos-init",
      animatedClassName: "aos-animate",
      useClassNames: false,
      disableMutationObserver: false,
      debounceDelay: 50,
      throttleDelay: 99,

      // Settings that can be overridden on per-element basis:
      offset: 300,
      delay: 0,
      duration: 700,
      easing: "ease-out-sine",
      once: true,
      mirror: false,
      anchorPlacement: "top-bottom",
    });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Router>
        <SearchMetadata />
        <AOSRouteRefresh />
        <ScrollToTop />
        <Box display="flex" flexDirection="column" minHeight="125vh">
          <Header />
          <Box component="main" flexGrow={1}>
            <Suspense fallback={<p className="search-container" role="status">Laster …</p>}>
              <Routes>
                <Route path="/" element={<Home />} />
                {publicPages.filter((page) => page.kind === "service").map((page) => (
                  <Route key={page.path} path={page.path} element={<SearchLandingPage />} />
                ))}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/checkout" element={<CartPage />} />
                <Route path="/pdf" element={<PDFViewPage />} />
                <Route path="/userrecovery" element={<UserRecovery />} />
                <Route
                  path="/userrecovery/:resetToken"
                  element={<PasswordResetPage />}
                />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/products/:productId" element={<ProductPage />} />
                <Route path="/order/:productId" element={<OrderWizardPage />} />
                <Route path="/bestilling/:token" element={<OrderConfirmationPage />} />
                <Route path="/magic-login" element={<MagicLoginPage />} />

                <Route path="/dashboard" element={<Dashboard />}>
                  <Route
                    path="motif"
                    element={
                      <ProtectedDashboardRoute>
                        <MyMotiv />
                      </ProtectedDashboardRoute>
                    }
                  />
                  <Route
                    path="motif/:motifId"
                    element={
                      <ProtectedDashboardRoute>
                        <MotifPage />
                      </ProtectedDashboardRoute>
                    }
                  />
                  <Route
                    path="motif/:motifId/edit"
                    element={
                      <ProtectedDashboardRoute>
                        <EditMotif />
                      </ProtectedDashboardRoute>
                    }
                  />
                  <Route
                    path="create-motif"
                    element={
                      <ProtectedDashboardRoute>
                        <CreateMotif />
                      </ProtectedDashboardRoute>
                    }
                  />
                  <Route
                    path="calculator"
                    element={
                      <ProtectedDashboardRoute>
                        <Calculator />
                      </ProtectedDashboardRoute>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <ProtectedDashboardRoute>
                        <SettingsPage />
                      </ProtectedDashboardRoute>
                    }
                  />
                  {/* <Route
                    path="profile"
                    element={
                      <ProtectedDashboardRoute>
                        <ProfilePage />
                      </ProtectedDashboardRoute>
                    }
                  /> */}
                  <Route
                    path="products"
                    element={
                      <ProtectedDashboardRoute>
                        <ProductsPage />
                      </ProtectedDashboardRoute>
                    }
                  />
                  <Route
                    path="order-history"
                    element={
                      <ProtectedDashboardRoute>
                        <OrderHistory />
                      </ProtectedDashboardRoute>
                    }
                  />
                  <Route
                    path="order/:orderId"
                    element={
                      <ProtectedDashboardRoute>
                        <OrderDetails />
                      </ProtectedDashboardRoute>
                    }
                  />
                  <Route
                    path="product/:productDesignId"
                    element={
                      <ProtectedDashboardRoute>
                        <ProductDesignPage />
                      </ProtectedDashboardRoute>
                    }
                  />

                  <Route
                    path="admin"
                    element={
                      <ProtectedDashboardRoute>
                        <AdminPage />
                      </ProtectedDashboardRoute>
                    }
                  >
                    <Route
                      path="all-motifs"
                      element={
                        <ProtectedDashboardRoute>
                          <AdminMyMotif />
                        </ProtectedDashboardRoute>
                      }
                    />
                    <Route
                      path="create-user"
                      element={
                        <ProtectedDashboardRoute>
                          <AdminCreateUser />
                        </ProtectedDashboardRoute>
                      }
                    />
                    <Route
                      path="price-chart"
                      element={
                        <ProtectedDashboardRoute>
                          <PriceChart />
                        </ProtectedDashboardRoute>
                      }
                    />
                    <Route
                      path="orders"
                      element={
                        <ProtectedDashboardRoute>
                          <AdminOrderHistory />
                        </ProtectedDashboardRoute>
                      }
                    />
                    <Route
                      path="users"
                      element={
                        <ProtectedDashboardRoute>
                          <UsersPage />
                        </ProtectedDashboardRoute>
                      }
                    />
                    <Route
                      path="companies"
                      element={
                        <ProtectedDashboardRoute>
                          <CompaniesPage />
                        </ProtectedDashboardRoute>
                      }
                    />
                    <Route
                      path="companies/:companyId"
                      element={
                        <ProtectedDashboardRoute>
                          <CompanyDetailsPage />
                        </ProtectedDashboardRoute>
                      }
                    />
                    <Route
                      path="all-products"
                      element={
                        <ProtectedDashboardRoute>
                          <AdminAllProducts />
                        </ProtectedDashboardRoute>
                      }
                    />
                  </Route>

                  <Route path="*" element={<NotFoundDash />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Box>
          <Footer />
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
